/**
 * Service quản lý các file mô phỏng GeoGebra (.ggb)
 * Tạm thời sử dụng danh sách tĩnh cho đến khi có API scan backend
 */

export interface GeoGebraContent {
    id: string;
    title: string;
    filename: string; // Tên file trong public/geogebra/
    lop: number;
    chuong: number;
    bai: number;
    description?: string;
    width?: number; // Optional custom width
    height?: number; // Optional custom height
}

// Danh sách các file GeoGebra đã đăng ký
const GEOGEBRA_REGISTRY: GeoGebraContent[] = [
    // Lớp 11 - Bài 6: Các đặc trưng của sóng (hoặc bài tương ứng trong chương 2)
    {
        id: 'ggb-l11-c2-b6-song-doc',
        title: 'Mô phỏng Sóng Dọc',
        filename: 'l11-b6-song-doc.ggb',
        lop: 11,
        chuong: 2,
        bai: 6,
        description: 'Mô phỏng sự lan truyền của sóng dọc trong lò xo',
        height: 600
    },
    {
        id: 'ggb-l11-c2-b6-song-ngang',
        title: 'Mô phỏng Sóng Ngang',
        filename: 'l11-b6-song-ngang.ggb',
        lop: 11,
        chuong: 2,
        bai: 6,
        description: 'Mô phỏng sự lan truyền của sóng ngang trên dây',
        height: 600
    },
    // Bổ sung file Boyle lấy ví dụ
    {
        id: 'ggb-l12-c2-b8-boyle',
        title: 'Định luật Boyle',
        filename: 'dl_Boyle.ggb', // File cũ
        lop: 12, // Giả sử map vào lớp 12 bài 8 (Khí lí tưởng)
        chuong: 2,
        bai: 8,
        description: 'Thí nghiệm ảo định luật Boyle',
    },

    // ===== Bổ sung theo PPCT (2026-05-31) =====
    // Lớp 10 — Chương 1 (Động học)
    {
        id: 'ggb-l10-c1-b7-bong-ro',
        title: 'Chuyển động ném (bóng rổ)',
        filename: 'l10-b7-bong-ro-nem-xien.ggb',
        lop: 10, chuong: 1, bai: 7,
        description: 'Mô phỏng chuyển động ném xiên qua quỹ đạo bóng rổ',
        height: 600,
    },
    // Lớp 11 — Chương 1 (Dao động)
    {
        id: 'ggb-l11-c1-b1-con-lac-lo-xo',
        title: 'Con lắc lò xo thẳng đứng',
        filename: 'l11-b1-con-lac-lo-xo-thang-dung.ggb',
        lop: 11, chuong: 1, bai: 1,
        description: 'Mô phỏng dao động điều hòa của con lắc lò xo thẳng đứng',
        height: 600,
    },
    {
        id: 'ggb-l11-c1-b2-x-v-a',
        title: 'Biểu diễn x, v, a theo thời gian',
        filename: 'l11-b2-bieu-dien-x-v-a.ggb',
        lop: 11, chuong: 1, bai: 2,
        description: 'Đồ thị li độ - vận tốc - gia tốc trong dao động điều hòa',
        height: 600,
    },
    {
        id: 'ggb-l11-c1-b3-nang-luong',
        title: 'Đồ thị năng lượng trong dao động',
        filename: 'l11-b3-do-thi-nang-luong.ggb',
        lop: 11, chuong: 1, bai: 3,
        description: 'Động năng - thế năng - cơ năng trong dao động điều hòa',
        height: 600,
    },
    {
        id: 'ggb-l11-c1-b4-tat-dan',
        title: 'Dao động điều hòa và dao động tắt dần',
        filename: 'l11-b4-dao-dong-tat-dan.ggb',
        lop: 11, chuong: 1, bai: 4,
        description: 'So sánh dao động điều hòa với dao động tắt dần',
        height: 600,
    },
    // Lớp 11 — Chương 2 (Sóng)
    {
        id: 'ggb-l11-c2-b6-doppler',
        title: 'Hiệu ứng Doppler',
        filename: 'l11-b6-doppler.ggb',
        lop: 11, chuong: 2, bai: 6,
        description: 'Mô phỏng hiệu ứng Doppler (thay đổi tần số sóng âm)',
        height: 600,
    },
    {
        id: 'ggb-l11-c2-b7-song-dien-tu',
        title: 'Sóng điện từ',
        filename: 'l11-b7-song-dien-tu.ggb',
        lop: 11, chuong: 2, bai: 7,
        description: 'Mô phỏng sự lan truyền của sóng điện từ',
        height: 600,
    },
    {
        id: 'ggb-l11-c2-b8-giao-thoa',
        title: 'Giao thoa sóng',
        filename: 'l11-b8-giao-thoa.ggb',
        lop: 11, chuong: 2, bai: 8,
        description: 'Mô phỏng giao thoa của hai nguồn sóng kết hợp',
        height: 600,
    },
    {
        id: 'ggb-l11-c2-b9-song-dung',
        title: 'Sóng dừng',
        filename: 'l11-b9-song-dung.ggb',
        lop: 11, chuong: 2, bai: 9,
        description: 'Mô phỏng sóng dừng trên dây với các nút và bụng sóng',
        height: 600,
    },
    // Lớp 12 — Chương 3 (Từ trường / dòng điện xoay chiều)
    {
        id: 'ggb-l12-c3-b15-may-phat',
        title: 'Máy phát điện xoay chiều một pha',
        filename: 'l12-b15-may-phat-1-pha.ggb',
        lop: 12, chuong: 3, bai: 15,
        description: 'Nguyên lý máy phát điện xoay chiều một pha',
        height: 600,
    },
];

export const GeoGebraService = {
    /**
     * Lấy danh sách GeoGebra cho một bài học cụ thể
     */
    getByLesson: (lop: number, chuong: number, bai: number): GeoGebraContent[] => {
        return GEOGEBRA_REGISTRY.filter(item =>
            item.lop === lop &&
            item.chuong === chuong &&
            item.bai === bai
        );
    },

    /**
     * Lấy tất cả file GeoGebra
     */
    getAll: (): GeoGebraContent[] => {
        return GEOGEBRA_REGISTRY;
    },

    /**
     * Tạo đường dẫn đầy đủ tới file trong public
     */
    getPublicPath: (filename: string): string => {
        // Đảm bảo không bị double slash
        const cleanName = filename.startsWith('/') ? filename.substring(1) : filename;
        return `/geogebra/${cleanName}`;
    }
};
